import React from 'react'
import { sdk } from '@/lib/sdk'
import { getApiOrigin } from '@/lib/sdk-official/loader'
import type { PushConfig, PushPlatform } from '@/lib/sdk-official/resources/push'
import { browserKind, currentPermission, detectPlatform, iosVersion, isInAppBrowser, isStandalone, pushApisPresent, type BrowserKind } from './platform'
import { isOptedOut, setOptedOut } from './session'

/**
 * F-186: this browser's push state, shared by the prompts (modal and bar), the
 * avatar panel item and the Minha conta card. One module-level store; React
 * reads it with `usePushState()`.
 *
 * Rules that matter:
 * - `Notification.requestPermission()` runs only inside `enable()`, which is
 *   only ever called from a click. Loading a page never asks.
 * - With the permission already granted, opening the app re-subscribes in
 *   silence when the API has no row for this device or its VAPID key changed
 *   (unless the person removed this device by hand).
 * - Logout removes this device's row and the browser subscription.
 */

/** What the prompts should ask for, in order. */
export type PushPhase =
  | 'off' // not logged in, not loaded, or push off in this environment
  | 'install' // install first (iPhone/iPad in Safari; Android/desktop Chromium with the install prompt at hand)
  | 'openInstalled' // iPhone Safari, but the person already has the installed app with push
  | 'enable' // installed or nothing to install, push not on in this browser
  | 'blocked' // denied in the browser
  | 'unsupported' // in-app browser, old iOS, browser without push
  | 'done'

export type UnsupportedReason = 'inApp' | 'oldIos' | 'browser'

export interface PushState {
  phase: PushPhase
  unsupported: UnsupportedReason | null
  platform: PushPlatform
  kind: BrowserKind
  standalone: boolean
  /** The browser offered its install prompt (`beforeinstallprompt`): "Instalar" can open it. */
  canPromptInstall: boolean
  permission: NotificationPermission | 'unsupported'
  config: PushConfig | null
  /** This browser's row in the API, when subscribed. */
  deviceId: string | null
  endpoint: string | null
  busy: boolean
}

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const INSTALLED_KEY = 'memberfy:appInstalled'
const SW_URL = '/sw.js'

let config: PushConfig | null = null
let deviceId: string | null = null
let endpoint: string | null = null
let busy = false
let installPrompt: InstallPromptEvent | null = null
let installed = (() => {
  try { return localStorage.getItem(INSTALLED_KEY) === '1' } catch { return false }
})()
let loadedFor = ''
/**
 * The prompts decide once: the phase stays 'off' (nothing rendered) until the
 * config, this device's reconciliation and the install-prompt grace are all
 * in, so the bar never flashes a wrong variant or shows-hides-shows.
 */
let settled = false
/** The install variant counts only if the browser offered it before settling; a late offer only arms the button. */
let installEligible = false
let loadToken = 0
/** How long after load a Chromium browser gets to fire `beforeinstallprompt` before the phase is decided. */
const INSTALL_GRACE_MS = 1500
const loadedAt = typeof performance !== 'undefined' ? performance.now() : 0
let state: PushState = compute()
const listeners = new Set<() => void>()

function compute(): PushState {
  const platform = detectPlatform()
  const standalone = isStandalone()
  const permission = currentPermission()
  const base = {
    unsupported: null as UnsupportedReason | null,
    platform,
    kind: browserKind(),
    standalone,
    canPromptInstall: Boolean(installPrompt),
    permission,
    config,
    deviceId,
    endpoint,
    busy,
  }
  const phase = ((): PushPhase => {
    if (!settled || !config?.available) return 'off'
    if (isInAppBrowser()) { base.unsupported = 'inApp'; return 'unsupported' }
    if (platform === 'ios') {
      const version = iosVersion()
      if (version !== null && version < 16.4) { base.unsupported = 'oldIos'; return 'unsupported' }
      if (!standalone) return config.hasInstalledIos ? 'openInstalled' : 'install'
    }
    if (!pushApisPresent()) {
      base.unsupported = platform === 'ios' ? 'oldIos' : 'browser'
      return 'unsupported'
    }
    if (deviceId && permission === 'granted') return 'done'
    if (permission === 'denied') return 'blocked'
    if (installEligible && installPrompt && !standalone && !installed) return 'install'
    return 'enable'
  })()
  return { ...base, phase }
}

const emit = () => {
  state = compute()
  for (const listener of listeners) listener()
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export const getPushState = () => state

export const usePushState = (): PushState => React.useSyncExternalStore(subscribe, getPushState, getPushState)

/* The install prompt and the "installed" signal (Android and desktop Chromium). */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    installPrompt = event as InstallPromptEvent
    emit()
  })
  window.addEventListener('appinstalled', () => {
    installed = true
    installPrompt = null
    try { localStorage.setItem(INSTALLED_KEY, '1') } catch { /* kept in memory */ }
    emit()
  })
}

const base64UrlToBytes = (value: string): Uint8Array => {
  const padded = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded)
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i)
  return bytes
}

const sameKey = (subscription: PushSubscription, publicKey: string): boolean => {
  const current = subscription.options?.applicationServerKey
  if (!current) return true // the browser does not say: trust the API's vapidKeyId
  const a = new Uint8Array(current)
  const b = base64UrlToBytes(publicKey)
  return a.length === b.length && a.every((byte, i) => byte === b[i])
}

const registration = async (): Promise<ServiceWorkerRegistration> => {
  const url = `${SW_URL}?api=${encodeURIComponent(getApiOrigin())}`
  await navigator.serviceWorker.register(url, { scope: '/' })
  return navigator.serviceWorker.ready
}

/** Sends this browser's subscription to the API (create or renew). */
const record = async (subscription: PushSubscription) => {
  const json = subscription.toJSON()
  const row = await sdk.push.subscribe({
    endpoint: json.endpoint as string,
    keys: { p256dh: json.keys?.p256dh as string, auth: json.keys?.auth as string },
    ...(config?.vapidKeyId ? { vapidKeyId: config.vapidKeyId } : {}),
    platform: detectPlatform(),
    standalone: isStandalone(),
  })
  deviceId = row?.id || null
  endpoint = json.endpoint || null
}

/** The browser subscription for the current key, made if missing (needs the permission already granted). */
const browserSubscription = async (): Promise<PushSubscription | null> => {
  if (!config?.publicKey) return null
  const reg = await registration()
  let subscription = await reg.pushManager.getSubscription()
  if (subscription && !sameKey(subscription, config.publicKey)) {
    await subscription.unsubscribe().catch(() => false)
    subscription = null
  }
  if (!subscription) {
    subscription = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(config.publicKey) })
  }
  return subscription
}

/**
 * Loads the config and reconciles this browser with the API. Called when the
 * session and the community are known; `key` (community + member) avoids
 * running twice for the same session.
 */
/** Chromium may still offer its install prompt: wait (at most the grace after load) before deciding. */
const installGrace = async () => {
  if (installPrompt || isStandalone() || installed || detectPlatform() === 'ios') return
  if (!/Chrome|Chromium|Edg|SamsungBrowser|OPR/i.test(navigator.userAgent)) return
  const left = INSTALL_GRACE_MS - (performance.now() - loadedAt)
  if (left > 0) await new Promise((resolve) => setTimeout(resolve, left))
}

/** This device's subscription against the API (silent: the permission is already granted). */
const reconcile = async (current: PushConfig) => {
  if (!pushApisPresent() || currentPermission() !== 'granted') return
  if (detectPlatform() === 'ios' && !isStandalone()) return
  try {
    const reg = await navigator.serviceWorker.getRegistration('/')
    const existing = reg ? await reg.pushManager.getSubscription() : null
    if (!existing && isOptedOut()) return
    const subscription = existing && current.publicKey && sameKey(existing, current.publicKey) ? existing : (isOptedOut() ? null : await browserSubscription())
    if (!subscription) return
    const devices = await sdk.push.listDevices(subscription.endpoint)
    const mine = devices.find((d) => d.current)
    if (mine && mine.vapidKeyId === current.vapidKeyId) {
      deviceId = mine.id
      endpoint = subscription.endpoint
    } else {
      await record(subscription)
    }
  } catch (error) {
    console.warn('[push] could not reconcile this device', error)
  }
}

/**
 * Loads the config and reconciles this browser with the API, then decides the
 * phase once (one `emit`). Called when the session and the community are
 * known; `key` (community + member) avoids running twice for the same session.
 */
export const refreshPush = async (key: string, force = false) => {
  if (!key) {
    if (!loadedFor && !settled) return
    loadToken += 1
    loadedFor = ''
    settled = false
    config = null
    deviceId = null
    endpoint = null
    emit()
    return
  }
  if (loadedFor === key && !force) return
  loadedFor = key
  const token = ++loadToken
  let next: PushConfig | null = null
  try {
    next = await sdk.push.config()
  } catch {
    next = null
  }
  if (token !== loadToken) return
  config = next
  deviceId = null
  endpoint = null
  if (next?.available) await Promise.all([reconcile(next), installGrace()])
  if (token !== loadToken) return
  installEligible = Boolean(installPrompt)
  settled = true
  emit()
}

export type EnableResult = 'enabled' | 'denied' | 'dismissed' | 'failed'

/**
 * "Ativar": asks the browser (the only place that does) and subscribes. Must be
 * called straight from a click handler: the permission request goes first,
 * before any other await, or Safari and Chrome treat it as unprompted.
 */
export const enablePush = async (): Promise<EnableResult> => {
  if (!pushApisPresent()) return 'failed'
  const asking = Notification.permission === 'granted' ? Promise.resolve<NotificationPermission>('granted') : Notification.requestPermission()
  busy = true
  emit()
  try {
    const permission = await asking
    if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'dismissed'
    if (!config) config = await sdk.push.config()
    setOptedOut(false)
    const subscription = await browserSubscription()
    if (!subscription) return 'failed'
    await record(subscription)
    return 'enabled'
  } catch (error) {
    console.warn('[push] enabling failed', error)
    return 'failed'
  } finally {
    busy = false
    emit()
  }
}

/** "Instalar" on Android and desktop Chromium: the browser's own install prompt. */
export const promptInstall = async (): Promise<boolean> => {
  const event = installPrompt
  if (!event) return false
  installPrompt = null
  emit()
  try {
    await event.prompt()
    const choice = await event.userChoice
    if (choice.outcome === 'accepted') {
      installed = true
      try { localStorage.setItem(INSTALLED_KEY, '1') } catch { /* kept in memory */ }
    }
    emit()
    return choice.outcome === 'accepted'
  } catch {
    emit()
    return false
  }
}

/** A device row was removed in Minha conta; when it is this browser, the browser subscription goes too. */
export const forgetDevice = async (id: string) => {
  if (id !== deviceId) return
  deviceId = null
  setOptedOut(true)
  try {
    const reg = await navigator.serviceWorker.getRegistration('/')
    const subscription = reg ? await reg.pushManager.getSubscription() : null
    await subscription?.unsubscribe()
  } catch {
    /* The API row is gone already; the browser one stops on the next 404/410. */
  }
  emit()
}

/**
 * Logout: this device's row goes (with the token and community captured now,
 * before the session is cleared) and the browser stops the subscription.
 * Fire and forget: logout never waits on it.
 */
export const pushLogout = (token: string | null, communityId: string) => {
  const id = deviceId
  deviceId = null
  endpoint = null
  config = null
  loadedFor = ''
  settled = false
  loadToken += 1
  emit()
  if (id && token && communityId) {
    void sdk.push.removeDevice(id, { Authorization: `Bearer ${token}`, 'X-CommunityId': communityId }).catch(() => {})
  }
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    void navigator.serviceWorker.getRegistration('/')
      .then((reg) => reg?.pushManager.getSubscription())
      .then((subscription) => subscription?.unsubscribe())
      .catch(() => {})
  }
}

/** A permission change made outside the app (site settings) shows up when the tab comes back. */
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && state.permission !== currentPermission()) emit()
  })
}
